import { FC, useMemo, useState } from "react";

import { Input } from "@/components/_atoms/input";
import { useFeatureFlagSimple } from "@/domain/_shared/feature-flag/hooks/api/use-feature-flag-simple";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { useTeamListForWorkspace } from "@/domain/team/hooks/api/use-team-list-for-workspace";

import { TeamBrowseListItem } from "./team-browse-list-item";

interface TeamBrowseListProps {
  workspaceId: string;
  userId: string;
}

export const TeamBrowseList: FC<TeamBrowseListProps> = ({ workspaceId, userId }) => {
  const [queryString, setQueryString] = useState("");
  const { t } = useDomainTranslation("team");
  const { isEnabled: isPermissionV2Enabled } = useFeatureFlagSimple({ id: "enable-permission-v2" });

  const { teamList } = useTeamListForWorkspace({
    workspaceId,
  });

  const filteredList = useMemo(() => {
    return teamList
      .slice()
      .filter((t) => {
        if (queryString === "") {
          return true;
        }
        return t.name.toLowerCase().indexOf(queryString.toLowerCase()) !== -1; // matching the querystring
      })
      .sort((a) => {
        if (a.teamMembershipList.some((tm) => tm.userId === userId && tm.status === "ACTIVE")) {
          return 1;
        }
        return -1;
      })
      .sort((a) => {
        if (a.teamMembershipList.some((tm) => tm.userId === userId && tm.status === "PENDING")) {
          return -1;
        }
        return 1;
      })
      .sort((a) => {
        // When permission v2 is enabled, skip visibility-based sorting — team browsing
        // is controlled by entity-level access instead of visibility
        if (isPermissionV2Enabled) {
          return 0;
        }
        if (a.visibility !== "PUBLIC" && !a.teamMembershipList.some((tm) => tm.userId === userId)) {
          return 1;
        }
        if (a.teamMembershipList.some((tm) => tm.userId === userId && tm.status === "ACTIVE")) {
          return 1;
        }
        return -1;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamList, queryString, isPermissionV2Enabled]);

  return (
    <div className="flex flex-col gap-2">
      <div className="mr-4">
        <Input
          value={queryString}
          onChange={(e) => setQueryString(e.currentTarget.value)}
          className="shadow-none border-none pl-0 text-xl font-light outline-hidden ring-0 focus-visible:border-none focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-focus"
          placeholder={t("browse.searchPlaceholder")}
          aria-label={t("browse.searchPlaceholder")}
          autoFocus
        />
      </div>
      <div className="h-0 w-full border-b"></div>
      <div className="flex max-h-[240px] flex-col overflow-y-scroll">
        {filteredList.map((team) => {
          return <TeamBrowseListItem key={team.id} workspaceId={workspaceId} teamId={team.id} userId={userId} />;
        })}
      </div>
    </div>
  );
};
